/**
 * @okrapdf/design-system — semantic tokens for all okraPDF clients.
 *
 * RULE: Never hardcode colors or icon names in client code.
 * Look up the token here, resolve to your platform's native format.
 *
 * Usage (React):
 *   import { token, tw, entityColor } from '@okrapdf/design-system';
 *   const phase = token('documentPhase', 'parsing');
 *   <span className={tw(phase.color)}>{phase.label}</span>
 *
 * Usage (overlays):
 *   import { entityColor } from '@okrapdf/design-system';
 *   const c = entityColor('table');
 *   // c.hex '#3b82f6', c.border 'rgba(59,130,246,0.9)', c.fill 'rgba(59,130,246,0.15)'
 *
 * Usage (Swift):
 *   Read tokens.json at build time or mirror SemanticTag enum.
 */

import tokens from './tokens.json';
import { hexToRgba } from './helpers';

// ---------------------------------------------------------------------------
// Token types
// ---------------------------------------------------------------------------

export interface Token {
  label: string;
  icon: string;     // SF Symbol (Swift)
  lucide: string;   // Lucide icon name (React)
  color: string;    // palette key
  pulse?: boolean;  // animated indicator
}

export interface EntityToken {
  label: string;
  icon: string;
  lucide: string;
  color: string;
  hex: string;
}

export interface ConfidenceToken {
  label: string;
  color: string;
  hex: string;
  threshold: number;
}

export interface PaletteEntry {
  hex: string;
  bg: string;
  text: string;
  border: string;
  ring: string;
  dot: string;
}

export interface TypographyToken {
  family: string;
  google: boolean;
  weights: number[];
  role: string;
}

// ---------------------------------------------------------------------------
// Entity overlay colors (hex + computed rgba for canvas/PDF rendering)
// ---------------------------------------------------------------------------

export interface EntityColor {
  hex: string;
  border: string;   // rgba at 0.9 opacity
  fill: string;     // rgba at 0.15 opacity
  label: string;
  icon: string;     // SF Symbol
  lucide: string;   // lucide icon name
  tailwind: { bg: string; border: string };
}

const ENTITIES = tokens.entity as Record<string, EntityToken>;
const PALETTE = tokens.palette as unknown as Record<string, PaletteEntry>;

/** Get entity overlay color with computed rgba values for canvas rendering. */
export function entityColor(type: string): EntityColor {
  const t = ENTITIES[type];
  if (!t) {
    return {
      hex: '#6b7280', border: 'rgba(107,114,128,0.9)', fill: 'rgba(107,114,128,0.15)',
      label: type, icon: 'questionmark.circle', lucide: 'help-circle',
      tailwind: { bg: 'bg-gray-500', border: 'border-gray-500' },
    };
  }
  const p = PALETTE[t.color];
  return {
    hex: t.hex,
    border: hexToRgba(t.hex, 0.9),
    fill: hexToRgba(t.hex, 0.15),
    label: t.label,
    icon: t.icon,
    lucide: t.lucide,
    tailwind: { bg: p?.dot ?? 'bg-gray-500', border: `border-${t.color}-500` },
  };
}

/**
 * ENTITY_COLORS — backwards-compatible object matching the shape from entity-colors.ts.
 * Prefer entityColor() for new code.
 */
export const ENTITY_COLORS = Object.fromEntries(
  Object.keys(ENTITIES).map((key) => [key, entityColor(key)])
) as Record<string, EntityColor>;

export type EntityType = keyof typeof tokens.entity;

// ---------------------------------------------------------------------------
// Token lookup
// ---------------------------------------------------------------------------

type TokenCategory =
  | 'documentPhase' | 'jobStatus' | 'verification' | 'contextType'
  | 'taskPriority' | 'privacyTier' | 'nodeStatus' | 'entity'
  | 'confidence' | 'fieldCategory' | 'pluginCategory' | 'registryItem'
  | 'featureMaturity';

const CATEGORIES = tokens as unknown as Record<string, Record<string, Token>>;

export function token(category: TokenCategory, value: string): Token {
  return CATEGORIES[category]?.[value] ?? { label: value, icon: 'questionmark.circle', lucide: 'help-circle', color: 'gray' };
}

// ---------------------------------------------------------------------------
// Confidence
// ---------------------------------------------------------------------------

const CONFIDENCE = tokens.confidence as Record<string, ConfidenceToken>;

export function getConfidenceLevel(score: number): 'high' | 'medium' | 'low' | 'critical' {
  if (score >= CONFIDENCE.high.threshold) return 'high';
  if (score >= CONFIDENCE.medium.threshold) return 'medium';
  if (score >= CONFIDENCE.low.threshold) return 'low';
  return 'critical';
}

export function confidenceToken(level: string): ConfidenceToken {
  return CONFIDENCE[level] ?? CONFIDENCE.critical;
}

export const CONFIDENCE_THRESHOLDS = {
  HIGH: CONFIDENCE.high.threshold,
  MEDIUM: CONFIDENCE.medium.threshold,
  LOW: CONFIDENCE.low.threshold,
  CRITICAL: CONFIDENCE.critical.threshold,
} as const;

// ---------------------------------------------------------------------------
// Tailwind class resolver
// ---------------------------------------------------------------------------

/** Get Tailwind classes for a color name: tw('blue') -> 'bg-blue-50 text-blue-700' */
export function tw(color: string): string {
  const p = PALETTE[color] ?? PALETTE.gray;
  return `${p.bg} ${p.text}`;
}

/** Full palette entry for a color */
export function palette(color: string): PaletteEntry {
  return PALETTE[color] ?? PALETTE.gray;
}

// ---------------------------------------------------------------------------
// Surface tokens — semantic app surfaces.
// Components should reference CSS vars (e.g. var(--paper-raised)) or call
// surface('raised').hex directly; styles injected once via surfaceCss().
// ---------------------------------------------------------------------------

export interface SurfaceToken {
  label: string;
  hex: string;
  darkHex?: string;
  cssVar: string;
}

export interface CssVariableToken {
  label?: string;
  hex?: string;
  darkHex?: string;
  value?: string;
  darkValue?: string;
  cssVar?: string;
}

export interface GrainToken {
  opacity: number;
}

const BRAND = tokens.brand as unknown as Record<string, CssVariableToken>;
const SURFACE = tokens.surface as unknown as Record<string, SurfaceToken>;
const COMPONENT = tokens.component as unknown as Record<string, CssVariableToken>;
const GRAIN = tokens.grain as unknown as Record<string, GrainToken>;
const TYPOGRAPHY = tokens.typography as unknown as Record<TypographyKey, TypographyToken>;

export type SurfaceKey =
  | 'page' | 'raised' | 'sunk'
  | 'ink' | 'inkSoft' | 'inkMute'
  | 'line' | 'lineSoft'
  | 'accent' | 'accentText' | 'accentTint'
  | 'success' | 'successGlow'
  | 'danger' | 'dangerText' | 'dangerTint' | 'dangerLine'
  | 'warning' | 'warningText' | 'warningTint' | 'warningLine'
  | 'info' | 'infoForeground' | 'infoText' | 'infoTint' | 'infoLine'
  | 'neutral' | 'neutralForeground' | 'neutralText' | 'neutralTint' | 'neutralLine'
  | 'overlay' | 'overlayStrong'
  | 'document' | 'documentLoading';

export type TypographyKey = 'sans' | 'serif' | 'mono';

const TYPOGRAPHY_FALLBACKS: Record<TypographyKey, string> = {
  sans: 'Inter Fallback, ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji"',
  serif: 'Inter Tight Fallback, Inter, ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
  mono: 'Source Code Pro Fallback, ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", monospace',
};

const TYPOGRAPHY_KEYS: TypographyKey[] = ['sans', 'serif', 'mono'];

export function surface(key: SurfaceKey): SurfaceToken {
  return SURFACE[key] ?? { label: key, hex: '#000', cssVar: `--${key}` };
}

export function typography(key: TypographyKey): TypographyToken {
  return TYPOGRAPHY[key] ?? { family: key, google: false, weights: [400], role: key };
}

/**
 * `var(--paper-raised, #ffffff)` — CSS var reference with hex fallback baked in
 * so components stay usable even before surfaceCss() is mounted.
 */
export function surfaceVar(key: SurfaceKey): string {
  const s = surface(key);
  return `var(${s.cssVar}, ${s.hex})`;
}

export function fontFamily(key: TypographyKey): string {
  const t = typography(key);
  return `"${t.family}", ${TYPOGRAPHY_FALLBACKS[key]}`;
}

export function fontFamilyVar(key: TypographyKey): string {
  return `var(--font-${key}, ${fontFamily(key)})`;
}

/** Per-token CSS overrides for a branded deploy (white-label / parsegrid.ai). */
export type CssVarOverride = {
  hex?: string;
  darkHex?: string;
  value?: string;
  darkValue?: string;
};

export type ThemeOverrides = {
  brand?: Record<string, CssVarOverride>;
  surface?: Record<string, CssVarOverride>;
  component?: Record<string, CssVarOverride>;
};

function mergeTokenMap<T extends Record<string, CssVariableToken>>(
  base: T,
  overrides?: Record<string, CssVarOverride>,
): T {
  if (!overrides) return base;
  const next = { ...base };
  for (const [key, override] of Object.entries(overrides)) {
    const current = next[key as keyof T];
    if (!current || typeof current !== 'object') continue;
    next[key as keyof T] = { ...current, ...override };
  }
  return next;
}

function surfaceCssLines(surface: Record<string, SurfaceToken> = SURFACE): string[] {
  return Object.values(surface)
    .filter((s) => s && typeof s === 'object' && s.cssVar)
    .map((s) => `  ${s.cssVar}: ${s.hex};`);
}

function cssVariableValue(token: CssVariableToken, mode: 'light' | 'dark'): string | undefined {
  if (mode === 'dark') return token.darkValue ?? token.darkHex;
  return token.value ?? token.hex;
}

function cssVariableLines(
  values: Record<string, CssVariableToken>,
  mode: 'light' | 'dark' = 'light',
): string[] {
  return Object.values(values)
    .filter((token): token is CssVariableToken & { cssVar: string } =>
      token != null && typeof token === 'object' && typeof token.cssVar === 'string',
    )
    .map((token) => {
      const value = cssVariableValue(token, mode);
      return value ? `  ${token.cssVar}: ${value};` : '';
    })
    .filter(Boolean);
}

function darkSurfaceCssLines(surface: Record<string, SurfaceToken> = SURFACE): string[] {
  return Object.values(surface)
    .filter((s): s is SurfaceToken & { darkHex: string } => !!s?.darkHex)
    .map((s) => `  ${s.cssVar}: ${s.darkHex};`);
}

function semanticCssLines(): string[] {
  return [
    '  --background: var(--paper);',
    '  --foreground: var(--ink);',
    '  --card: var(--paper-raised);',
    '  --card-foreground: var(--ink);',
    '  --popover: var(--paper-raised);',
    '  --popover-foreground: var(--ink);',
    '  --primary: var(--ember);',
    '  --primary-foreground: var(--paper-raised);',
    '  --primary-hover: var(--button-primary-bg-hover);',
    '  --secondary: var(--paper-sunk);',
    '  --secondary-foreground: var(--ink-soft);',
    '  --muted: var(--paper-sunk);',
    '  --muted-foreground: var(--ink-mute);',
    '  --accent: var(--ember-tint);',
    '  --accent-foreground: var(--ember-text);',
    // Navigation drawer surface (shadcn sidebar tokens): the muted canvas the
    // page card sits on, its row text, and the neutral active/hover row.
    '  --sidebar: var(--muted);',
    '  --sidebar-foreground: var(--secondary-foreground);',
    '  --sidebar-accent: var(--border);',
    '  --sidebar-accent-foreground: var(--foreground);',
    '  --sidebar-border: var(--border);',
    '  --selected: var(--paper-sunk);',
    '  --selected-foreground: var(--ink);',
    '  --selected-line: var(--ink);',
    '  --destructive: var(--danger);',
    '  --destructive-foreground: var(--paper-raised);',
    '  --border: var(--line);',
    '  --input: var(--line);',
    '  --ring: var(--ember);',
    '  --emerald: var(--success);',
    '  --emerald-glow: var(--success-glow);',
  ];
}

function shadowCssLines(): string[] {
  const SHADOW = tokens.shadow as unknown as Record<string, { cssVar?: string; value?: string }>;
  return Object.values(SHADOW)
    .filter((s): s is { cssVar: string; value: string } =>
      s != null && typeof s === 'object' && typeof s.cssVar === 'string' && typeof s.value === 'string',
    )
    .map((s) => `  ${s.cssVar}: ${s.value};`);
}

function radiusCssLines(): string[] {
  const RADIUS = tokens.radius as unknown as Record<string, { cssVar?: string; value?: string }>;
  return Object.values(RADIUS)
    .filter((r): r is { cssVar: string; value: string } =>
      r != null && typeof r === 'object' && typeof r.cssVar === 'string' && typeof r.value === 'string',
    )
    .map((r) => `  ${r.cssVar}: ${r.value};`);
}

function typographyCssLines(): string[] {
  return TYPOGRAPHY_KEYS.map((key) => `  --font-${key}: ${fontFamily(key)};`);
}

function rootCss(lines: string[]): string {
  return `:root {\n${lines.join('\n')}\n}`;
}

function darkScopeCss(lines: string[]): string {
  return `[data-theme='dark'] {\n${lines.join('\n')}\n}`;
}

function darkAliasCssLines(component: Record<string, CssVariableToken> = COMPONENT): string[] {
  return [
    ...semanticCssLines(),
    ...cssVariableLines(component),
    ...cssVariableLines(component, 'dark'),
  ];
}

function resolveTokenMaps(overrides?: ThemeOverrides): {
  brand: Record<string, CssVariableToken>;
  surface: Record<string, SurfaceToken>;
  component: Record<string, CssVariableToken>;
} {
  return {
    brand: mergeTokenMap(BRAND, overrides?.brand),
    surface: mergeTokenMap(SURFACE as Record<string, CssVariableToken>, overrides?.surface) as Record<string, SurfaceToken>,
    component: mergeTokenMap(COMPONENT, overrides?.component),
  };
}

function emitDesignTokensCss(
  maps: ReturnType<typeof resolveTokenMaps>,
  options: { includeShadowRadiusType: boolean },
): string {
  const extras = options.includeShadowRadiusType
    ? [...shadowCssLines(), ...radiusCssLines(), ...typographyCssLines()]
    : [];
  const light = rootCss([
    ...cssVariableLines(maps.brand),
    ...surfaceCssLines(maps.surface),
    ...semanticCssLines(),
    ...cssVariableLines(maps.component),
    ...extras,
  ]);
  const darkLines = darkSurfaceCssLines(maps.surface);
  const dark = [
    ...cssVariableLines(maps.brand, 'dark'),
    ...darkLines,
    ...darkAliasCssLines(maps.component),
  ];
  return dark.length ? `${light}\n${darkScopeCss(dark)}` : light;
}

/**
 * Emit `:root { --paper: #ffffff; ... }` from the canonical surface tokens.
 * Mount once at app startup so every component resolves vars consistently.
 */
export function surfaceCss(overrides?: ThemeOverrides): string {
  return emitDesignTokensCss(resolveTokenMaps(overrides), { includeShadowRadiusType: false });
}

/** Emit only font-family variables from canonical typography tokens. */
export function typographyCss(): string {
  return rootCss(typographyCssLines());
}

/** Emit all web CSS custom properties backed by tokens.json. */
export function designTokensCss(overrides?: ThemeOverrides): string {
  return emitDesignTokensCss(resolveTokenMaps(overrides), { includeShadowRadiusType: true });
}

/** Google Fonts stylesheet URL for the typography tokens that opt into Google hosting. */
export function googleFontsHref(): string {
  const families = TYPOGRAPHY_KEYS
    .map((key) => typography(key))
    .filter((t) => t.google)
    .map((t) => {
      const family = t.family.trim().replace(/\s+/g, '+');
      const weights = [...t.weights].sort((a, b) => a - b).join(';');
      return weights ? `family=${family}:wght@${weights}` : `family=${family}`;
    });

  if (families.length === 0) return '';
  return `https://fonts.googleapis.com/css2?${families.join('&')}&display=swap`;
}

export function grain(key: 'subtle' | 'medium'): GrainToken {
  return GRAIN[key] ?? { opacity: 0.14 };
}

// ---------------------------------------------------------------------------
// Direct section access
// ---------------------------------------------------------------------------

export { tokens };
export { hexToRgba } from './helpers';
